import { putRead, getRead } from './offlineReadCache';
import { applyPending, queuedResponse, offlineAllowed } from './offlineOverlay';
import { captureScope, newIdempotencyKey } from './outboxIdentity';

// writeOutbox is loaded LAZILY, and it has to be: it imports fetchJson in order
// to deliver queued writes, so importing it here statically closes a cycle.
// A bundler resolves a cycle by handing one side a half-initialised module, and
// the consts at the top of writeOutbox then throw on access — at load time, not
// at the point of use, which is why it takes the whole app down rather than one
// feature.
//
// The cost is nothing. This path only runs once a request has already failed,
// and the module is cached after the first load.
let outboxPromise = null;
function loadOutbox() {
  if (!outboxPromise) outboxPromise = import('./writeOutbox');
  return outboxPromise;
}

const parseStoredAcademicYearId = (raw) => {
  if (raw == null) return null;
  try {
    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      if (!trimmed) return null;
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        const obj = JSON.parse(trimmed);
        if (obj && typeof obj === 'object') {
          const candidate = obj.id ?? obj.value ?? obj.sessionId ?? obj.academicYearId ?? obj.ayId ?? obj.yearId;
          if (candidate != null && candidate !== '') {
            return String(candidate);
          }
        }
      }
      const num = parseInt(trimmed, 10);
      if (!Number.isNaN(num) && num > 0) {
        return String(num);
      }
    } else if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
      return String(raw);
    } else if (typeof raw === 'object' && raw) {
      const candidate = raw.id ?? raw.value ?? raw.sessionId ?? raw.academicYearId ?? raw.ayId ?? raw.yearId;
      if (candidate != null && candidate !== '') {
        return String(candidate);
      }
    }
  } catch (_) {}
  return null;
};

export function resolveApiUrl(path = '') {
  const configuredBaseRaw = (
    process.env.REACT_APP_API_BASE_URL ||
    process.env.VITE_API_BASE_URL ||
    process.env.NEXT_PUBLIC_API_BASE_URL ||
    ''
  ).trim();

  const browserHost = typeof window !== 'undefined' ? window.location.hostname : '';
  const isLocalBrowserHost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(browserHost);
  const isLocalNodeEnv = process.env.NODE_ENV === 'development';
  const sameOriginBase = (typeof window !== 'undefined' && window.location && window.location.origin)
    ? window.location.origin
    : '';

  if (!configuredBaseRaw && !isLocalBrowserHost && !isLocalNodeEnv && sameOriginBase) {
    console.error(
      '[fetchJson] REACT_APP_API_BASE_URL is not set. API calls will target the frontend origin ' +
      `(${sameOriginBase}) instead of the backend server. ` +
      'Set REACT_APP_API_BASE_URL in your Vercel environment variables.'
    );
  }

  const baseRaw = configuredBaseRaw
    || ((isLocalBrowserHost || isLocalNodeEnv) ? 'http://localhost:5000' : '')
    || sameOriginBase;

  if (!baseRaw) {
    const e = new Error('API base URL is not configured. Set REACT_APP_API_BASE_URL for deployed builds.');
    e.userMessage = 'Service temporarily unavailable. Please try again shortly.';
    throw e;
  }

  const isAbsolute = /^https?:\/\//i.test(path);
  let base = baseRaw.replace(/\/$/, '');
  let pathPart = path.startsWith('/') ? path : `/${path}`;

  if (!isAbsolute) {
    const baseEndsWithApi = /\/api$/i.test(base);
    const pathStartsWithApi = /^\/api(\b|\/)/i.test(pathPart);
    if (baseEndsWithApi && pathStartsWithApi) {
      pathPart = pathPart.replace(/^\/api/i, '');
    }
  }

  return isAbsolute ? path.replace(/([^:]\/)\/+/, '$1') : `${base}${pathPart}`.replace(/([^:]\/)\/+/, '$1');
}

export async function fetchJson(path, options = {}) {
  const isProd = process.env.NODE_ENV === 'production';

  const extractBackendMessage = (data) => {
    if (typeof data === 'string') return data.trim();
    if (!data || typeof data !== 'object') return '';
    return String(data.userMessage || data.publicMessage || data.error || data.message || '').trim();
  };

  const isSafeBackendMessage = (message) => {
    const raw = String(message || '').trim();
    if (!raw) return false;
    const lower = raw.toLowerCase();
    return !(
      lower.includes('sequelize') ||
      lower.includes('sql') ||
      lower.includes('syntaxerror') ||
      lower.includes('referenceerror') ||
      lower.includes('typeerror') ||
      lower.includes('stack') ||
      lower.includes('trace') ||
      lower.includes('enoent') ||
      lower.includes('econn')
    );
  };

  const toUserMessage = ({ status, data, networkError, preferBackendMessage = false }) => {
    if (networkError) {
      return 'Network error. Please check your internet connection and try again.';
    }

    const backendMessage = extractBackendMessage(data);
    if (preferBackendMessage && isSafeBackendMessage(backendMessage)) {
      return backendMessage;
    }

    const statusNum = Number(status);
    if (statusNum === 400) return 'Please check your input and try again.';
    if (statusNum === 401) return 'Your session has expired. Please log in again.';
    if (statusNum === 403) return 'You do not have permission to perform this action.';
    if (statusNum === 404) return 'The requested resource was not found.';
    if (statusNum === 408) return 'The request timed out. Please try again.';
    if (statusNum === 409) return 'This request could not be completed due to a conflict. Please refresh and try again.';
    if (statusNum === 413) return 'The uploaded file is too large. Please try a smaller file.';
    if (statusNum === 415) return 'Unsupported file format.';
    if (statusNum === 422) return 'Please check your input and try again.';
    if (statusNum === 429) return 'Too many attempts. Please wait a moment and try again.';
    if (statusNum >= 500) return 'Service temporarily unavailable. Please try again shortly.';

    if (isSafeBackendMessage(backendMessage)) return backendMessage;
    return 'Something went wrong. Please try again.';
  };

  const url = resolveApiUrl(path);

  // Inject Authorization header if token exists (try multiple common keys)
  const tokenKeys = ['impersonationToken', 'accessToken', 'token', 'authToken', 'jwt', 'id_token', 'access_token', 'ACCESS_TOKEN'];
  let token = null;
  for (const k of tokenKeys) {
    const v = localStorage.getItem(k) || sessionStorage.getItem(k);
    if (v) { token = v; break; }
  }
  // Try to extract token from stored user blob if present
  if (!token) {
    try {
      const sources = [localStorage.getItem('user'), sessionStorage.getItem('user')];
      for (const raw of sources) {
        if (!raw) continue;
        try {
          const obj = JSON.parse(raw);
          const candidateKeys = ['accessToken', 'token', 'authToken', 'jwt', 'id_token'];
          for (const ck of candidateKeys) {
            if (obj && obj[ck]) { token = obj[ck]; break; }
          }
          if (token) break;
        } catch (_) {}
      }
    } catch (_) {}
  }
  // Last resort: scan storages for any JWT-looking value
  if (!token) {
    const looksLikeJwt = (v) => typeof v === 'string' && /\S+\.\S+\.\S+/.test(v);
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        const val = localStorage.getItem(key);
        if (looksLikeJwt(val)) { token = val; break; }
      }
      if (!token) {
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          const val = sessionStorage.getItem(key);
          if (looksLikeJwt(val)) { token = val; break; }
        }
      }
    } catch (_) {}
  }
  options.headers = options.headers || {};
  
  // Sanitize ALL header values to ensure ISO-8859-1 compliance (remove non-ASCII chars)
  const sanitizeHeaderValue = (val) => {
    if (!val) return '';
    try {
      let s = String(val).trim();
      // Remove any non-ASCII characters (keep only 0x20-0x7E and some extended)
      // Remove control characters
      let out = '';
      for (let i = 0; i < s.length; i++) {
        const code = s.charCodeAt(i);
        if (code < 0x20 || code === 0x7F) continue;
        if (code > 0xFF) continue;
        out += s[i];
      }
      s = out;
      return s;
    } catch {
      return '';
    }
  };
  
  // Sanitize existing headers
  for (const key in options.headers) {
    if (options.headers.hasOwnProperty(key)) {
      options.headers[key] = sanitizeHeaderValue(options.headers[key]);
      if (!options.headers[key]) delete options.headers[key];
    }
  }
  
  // Allow callers to skip Authorization header entirely (for public endpoints/CORS)
  const skipAuth = options.skipAuth === true;
  let hasAuth = false;
  // If caller provided an Authorization header, sanitize it as well
  if (!skipAuth && options.headers['Authorization']) {
    try {
      let h = sanitizeHeaderValue(options.headers['Authorization']);
      if (h.toLowerCase().startsWith('bearer ')) h = h.slice(7).trim();
      h = h.replace(/^"+|"+$/g, '').replace(/^'+|'+$/g, '');
      const jwtUrlSafe = /^[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+$/; // base64url only
      if (jwtUrlSafe.test(h)) {
        options.headers['Authorization'] = `Bearer ${h}`;
        hasAuth = true;
      } else {
        delete options.headers['Authorization'];
      }
    } catch (_) {
      delete options.headers['Authorization'];
    }
  }
  if (!skipAuth && !hasAuth && token) {
    try {
      let t = sanitizeHeaderValue(token);
      // If already starts with 'Bearer', strip it so we can re-normalize
      if (t.toLowerCase().startsWith('bearer ')) {
        t = t.slice(7).trim();
      }
      // Strip surrounding quotes if the token was stored quoted
      t = t.replace(/^"+|"+$/g, '').replace(/^'+|'+$/g, '');
      // Validate JWT-friendly charset (header.payload.signature)
      const jwtUrlSafe = /^[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+$/; // base64url only
      if (jwtUrlSafe.test(t)) {
        options.headers['Authorization'] = `Bearer ${t}`;
        hasAuth = true;
      } else {
        // Skip setting header if it contains invalid characters
        if (!isProd) console.warn('[fetchJson] Skipping Authorization header: token has invalid characters', t.slice(0, 20));
      }
    } catch (e) {
      // Do not set Authorization on any parsing/validation errors
      if (!isProd) console.warn('[fetchJson] Skipping Authorization header due to parsing error', e);
    }
  }
  // Inject Active Phase header if SchoolAdmin has selected one (sanitized)
  const activePhase = sanitizeHeaderValue(localStorage.getItem('schoolAdmin_activePhase') || '');
  if (activePhase && !options.headers['X-Active-Phase']) {
    options.headers['X-Active-Phase'] = activePhase;
  }
  // Inject Active Phase ID (sanitized)
  const activePhaseId = sanitizeHeaderValue(localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId') || '');
  if (activePhaseId && !options.headers['X-Active-Phase-Id']) {
    options.headers['X-Active-Phase-Id'] = activePhaseId;
  }
  // Inject Academic Year header if available (silent frontend context)
  const scopedSchoolId = sanitizeHeaderValue(localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '');
  const schoolId = sanitizeHeaderValue(localStorage.getItem('schoolId') || '');
  const effectiveSchoolId = scopedSchoolId || schoolId;
  const namespacedKey = effectiveSchoolId ? `${effectiveSchoolId}:currentAcademicYearId` : 'currentAcademicYearId';
  const storedAcademicYearRaw = localStorage.getItem(namespacedKey) || localStorage.getItem('currentAcademicYearId');
  const parsedStoredAcademicYearId = parseStoredAcademicYearId(storedAcademicYearRaw);
  if (parsedStoredAcademicYearId && !options.headers['X-Academic-Year-Id']) {
    options.headers['X-Academic-Year-Id'] = parsedStoredAcademicYearId;
  }
  // Include School ID headers: preferred X-School-Id (guide), and legacy X-Scope-School-Id for compatibility
  if (effectiveSchoolId) {
    if (!options.headers['X-School-Id']) {
      options.headers['X-School-Id'] = effectiveSchoolId;
    }
    if (!options.headers['X-Scope-School-Id']) {
      options.headers['X-Scope-School-Id'] = effectiveSchoolId;
    }
  }

  // Inject scope headers if present (section- or school-scoped operations) - all sanitized
  // Accept multiple keys for flexibility in UI wiring
  const scopeLevel = sanitizeHeaderValue(localStorage.getItem('activeScopeLevel') || localStorage.getItem('scopeLevel') || '');
  const scopeSchoolId = scopedSchoolId;
  const scopeSectionId = sanitizeHeaderValue(localStorage.getItem('activeSectionId') || localStorage.getItem('scopeSectionId') || '');
  if (scopeLevel && !options.headers['X-Scope-Level']) {
    options.headers['X-Scope-Level'] = scopeLevel;
  }
  if (scopeSchoolId && !options.headers['X-Scope-School-Id']) {
    options.headers['X-Scope-School-Id'] = scopeSchoolId;
  }
  if (scopeSectionId && !options.headers['X-Scope-Section-Id']) {
    options.headers['X-Scope-Section-Id'] = scopeSectionId;
  }
  // Ensure mutating requests carry AY/Phase/School context — enforce for POST/PUT/PATCH/DELETE
  try {
    const method = (options.method || 'GET').toString().toUpperCase();
    const isMutating = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
    if (isMutating) {
      // Re-compute school/ay/phase from available storage if headers are missing
      const resolvedSchoolId = options.headers['X-Scope-School-Id'] || options.headers['X-School-Id'] || schoolId || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId');
      const resolvedPhase = options.headers['X-Active-Phase'] || localStorage.getItem('schoolAdmin_activePhase') || localStorage.getItem('activePhase');
      const resolvedPhaseId = options.headers['X-Active-Phase-Id'] || localStorage.getItem('schoolAdmin_activePhaseId') || localStorage.getItem('activePhaseId');
      const resolvedAy = options.headers['X-Academic-Year-Id']
        || parsedStoredAcademicYearId
        || parseStoredAcademicYearId(localStorage.getItem('currentGlobalAcademicYearId'))
        || parseStoredAcademicYearId(localStorage.getItem(resolvedSchoolId ? `${resolvedSchoolId}:currentAcademicYearId` : null));
      if (resolvedSchoolId && !options.headers['X-Scope-School-Id']) options.headers['X-Scope-School-Id'] = resolvedSchoolId;
      if (resolvedPhase && !options.headers['X-Active-Phase']) options.headers['X-Active-Phase'] = resolvedPhase;
      if (resolvedPhaseId && !options.headers['X-Active-Phase-Id']) options.headers['X-Active-Phase-Id'] = resolvedPhaseId;
      if (resolvedAy && !options.headers['X-Academic-Year-Id']) options.headers['X-Academic-Year-Id'] = String(resolvedAy);

      // If body is present and is JSON (object or parseable string), inject academicYearId/schoolId/phaseId into it
      const isFormData = (typeof FormData !== 'undefined') && (options.body instanceof FormData);
      if (!isFormData && options.body) {
        try {
          let bodyObj = null;
          if (typeof options.body === 'string') {
            try { bodyObj = JSON.parse(options.body); } catch (_) { bodyObj = null; }
          } else if (typeof options.body === 'object') {
            bodyObj = options.body;
          }
          if (bodyObj && typeof bodyObj === 'object') {
            let mutated = false;
            // Only inject legacy academicYearId when the caller did not provide globalAcademicYearId.
            // Many newer endpoints store the global academic year in `globalAcademicYearId`, and injecting
            // it into `academicYearId` can violate FK constraints against `academic_years`.
            if (
              resolvedAy &&
              (bodyObj.globalAcademicYearId === undefined || bodyObj.globalAcademicYearId === null) &&
              (bodyObj.academicYearId === undefined || bodyObj.academicYearId === null)
            ) {
              bodyObj.academicYearId = Number(resolvedAy);
              mutated = true;
            }
            if (resolvedSchoolId && (bodyObj.schoolId === undefined || bodyObj.schoolId === null)) { bodyObj.schoolId = Number(resolvedSchoolId); mutated = true; }
            if (resolvedPhaseId && (bodyObj.phaseId === undefined || bodyObj.phaseId === null)) { bodyObj.phaseId = Number(resolvedPhaseId); mutated = true; }
            if (mutated) {
              options.body = JSON.stringify(bodyObj);
              if (!options.headers['Content-Type']) options.headers['Content-Type'] = 'application/json';
            }
          }
        } catch (_) {}
      }
    }
  } catch (_) {}
  // Auto-set JSON headers and stringify body when sending JSON (but do NOT touch FormData)
  const isFormData = (typeof FormData !== 'undefined') && (options.body instanceof FormData);
  if (options.body && !isFormData) {
    // If body is a plain object, stringify it
    if (typeof options.body === 'object' && typeof options.body !== 'string') {
      options.body = JSON.stringify(options.body);
    }
    // Set Content-Type if not already set
    if (!options.headers['Content-Type']) {
      options.headers['Content-Type'] = 'application/json';
    }
  }

  // Always prefer JSON responses by default
  if (!options.headers['Accept']) {
    options.headers['Accept'] = 'application/json';
  }
  if (!options.headers['X-Requested-With']) {
    options.headers['X-Requested-With'] = 'XMLHttpRequest';
  }


  // Default to include credentials (cookie-based auth) alongside Bearer when available
  if (!('credentials' in options)) {
    options.credentials = 'include';
  }

  let res;
  // Final sanitization pass: ensure ALL header values are ISO-8859-1 safe before fetch
  try {
    for (const key in options.headers) {
      if (options.headers.hasOwnProperty(key)) {
        const val = options.headers[key];
        if (val !== null && val !== undefined) {
          const sanitized = sanitizeHeaderValue(String(val));
          if (sanitized) {
            options.headers[key] = sanitized;
          } else {
            delete options.headers[key];
          }
        } else {
          delete options.headers[key];
        }
      }
    }
  } catch (e) {
    if (!isProd) console.warn('[fetchJson] Header sanitization error:', e);
  }
  
  // Final guard: never send Authorization to public top-level diagnostics
  try {
    if (/\/api\/classes\/(summary-public-top|recent-top|ping-top)(\b|\?|$)/.test(url)) {
      if (options.headers && options.headers['Authorization']) {
        delete options.headers['Authorization'];
      }
    }
    res = await fetch(url, options);
  } catch (err) {
    // THE NETWORK IS GONE. Before failing, see whether this request can be
    // answered from what we already have, or queued for when it comes back.
    //
    // Doing it HERE rather than page by page is the whole point: every screen
    // already goes through this function, so one hook makes the product work
    // offline instead of the two pages that had been wired by hand.
    const method = String(options.method || 'GET').toUpperCase();

    if (offlineAllowed(path, method)) {
      if (method === 'GET') {
        try {
          const hit = await getRead(url, options.headers || {});
          if (hit) {
            // Queued writes are folded in, so a class added five minutes ago
            // while offline is in the list being looked at now.
            const { pendingOverlayWrites } = await loadOutbox();
            const pending = await pendingOverlayWrites().catch(() => []);
            const body = applyPending(path, hit.body, pending);
            if (body !== null) {
              // Marked, not disguised, so a page can say how old this is.
              try {
                Object.defineProperty(body, '__fromCache', { value: true, enumerable: false });
                Object.defineProperty(body, '__cachedAt', { value: hit.savedAt, enumerable: false });
              } catch (_) { /* frozen or primitive — the body still stands */ }
              return body;
            }
          }
        } catch (_) { /* fall through to the error below */ }
      } else if (!isQueueableBody(options.body)) {
        // A file upload. Not queued: it cannot be persisted or replayed, and a
        // queued upload would go out later with an empty body and quietly
        // replace a document with nothing. Falls through and says so.
      } else {
        try {
          const { queueWrite, WRITE_KINDS } = await loadOutbox();
          const rec = await queueWrite({
            kind: offlineKindFor(path, WRITE_KINDS),
            endpoint: path,
            method,
            body: parseBodyForQueue(options.body),
            label: `${method} ${path}`,
            idempotencyKey: newIdempotencyKey(),
            scope: captureScope(),
          });
          if (rec && rec.id != null) {
            return queuedResponse({
              method, endpoint: path,
              body: parseBodyForQueue(options.body),
              outboxId: rec.id,
            });
          }
        } catch (_) { /* could not queue — fall through and report honestly */ }
      }
    }

    // Never leak raw browser/URL/network details to users.
    const wrapped = new Error('Network request failed');
    wrapped.cause = err;
    if (!isProd) wrapped.attemptedUrl = url;
    wrapped.userMessage = toUserMessage({ networkError: true });
    throw wrapped;
  }

  // Handle HTTP errors first
  if (!res.ok) {
    let errorDetails;
    try {
      // Try to parse error response as JSON
      errorDetails = await res.clone().json();
    } catch {
      // Fallback to text if JSON parsing fails
      errorDetails = await res.text();
    }

    // Subscription enforcement: raise a global signal so the UI can display a persistent banner.
    try {
      const rawErr = (
        (errorDetails && typeof errorDetails === 'object' && (errorDetails.error || errorDetails.message))
          ? (errorDetails.error || errorDetails.message)
          : (typeof errorDetails === 'string' ? errorDetails : '')
      );
      const msg = (rawErr || '').toString().toLowerCase();
      const isSubExpired = msg.includes('subscription expired') || msg.includes('renew your subscription');
      if (isSubExpired) {
        try { localStorage.setItem('app_subscription_expired', 'true'); } catch (_) {}
        try {
          window.dispatchEvent(new CustomEvent('app:subscription:expired', { detail: { url, status: res.status } }));
        } catch (_) {}
      }
    } catch (_) {}

    // Special handling for 401 to help the UI take action
    const autoRedirectFlag = (process.env.REACT_APP_AUTO_REDIRECT_ON_401 || 'false') === 'true';
    if (res.status === 401 && !skipAuth) {
      if (autoRedirectFlag) {
        try {
          window.dispatchEvent(new CustomEvent('app:auth:required', { detail: { url } }));
        } catch (_) {}
      }
      // Retry once with credentials included (in case they were stripped by caller)
      try {
        const retryOptions = { ...options, credentials: 'include' };
        const retryRes = await fetch(url, retryOptions);
        if (retryRes.ok) {
          if (retryRes.status === 204) return null;
          const retryContentLength = retryRes.headers.get('content-length');
          if (retryContentLength === '0') return null;
          const retryRawBody = await retryRes.text();
          if (!retryRawBody) return null;
          try {
            return JSON.parse(retryRawBody);
          } catch (_) {
            return retryRawBody;
          }
        }
      } catch (_) {}

      try {
        const storedRefresh = (localStorage.getItem('refreshToken') || localStorage.getItem('REFRESH_TOKEN') || sessionStorage.getItem('refreshToken') || '');
        const doRefresh = async (u) => fetch(u, { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(storedRefresh ? { refreshToken: storedRefresh } : {}) });
        let refreshRes = await doRefresh(resolveApiUrl('/api/auth/refresh-token'));
        if (!refreshRes.ok) {
          refreshRes = await doRefresh(resolveApiUrl('/auth/refresh-token'));
        }
        if (refreshRes.ok) {
          const data = await refreshRes.json();
          if (data && data.accessToken) {
            try { localStorage.setItem('token', data.accessToken); } catch (_) {}
            try { localStorage.setItem('accessToken', data.accessToken); } catch (_) {}
            // THE ROTATED REFRESH TOKEN, which is the half that makes the
            // session last. The server issues a new one on every refresh so the
            // expiry window slides forward from last use; keeping the old one
            // here would throw that away and the session would still die on a
            // fixed date, thirty days after signing in, however much the app
            // had been used since.
            if (data.refreshToken) {
              try { localStorage.setItem('refreshToken', data.refreshToken); } catch (_) {}
            }
            const retryHeaders = { ...(options.headers || {}), Authorization: `Bearer ${data.accessToken}` };
            const retryRes2 = await fetch(url, { ...options, headers: retryHeaders, credentials: 'include' });
            if (retryRes2.ok) {
              if (retryRes2.status === 204) return null;
              const retry2ContentLength = retryRes2.headers.get('content-length');
              if (retry2ContentLength === '0') return null;
              const retry2RawBody = await retryRes2.text();
              if (!retry2RawBody) return null;
              try {
                return JSON.parse(retry2RawBody);
              } catch (_) {
                return retry2RawBody;
              }
            }
          }
        }
      } catch (_) {}
    }

    const buildErrorSummary = (details) => {
      if (typeof details === 'string') return details.slice(0, 200);
      if (!details || typeof details !== 'object') return res.statusText;

      const parts = [];
      const main = details.error || details.message || res.statusText;
      if (main) parts.push(String(main));
      if (details.page) parts.push(`page=${details.page}`);
      if (details.userRole) parts.push(`userRole=${details.userRole}`);
      if (details.rawRole) parts.push(`rawRole=${details.rawRole}`);
      return parts.join(' | ').slice(0, 300);
    };

    const preferBackendMessage = options.skipAuth === true || /\/auth(\/|\?|$)/i.test(url);
    const error = new Error(`HTTP ${res.status}: ${buildErrorSummary(errorDetails)}`);
      
    error.response = res;
    error.data = errorDetails;
    error.userMessage = toUserMessage({ status: res.status, data: errorDetails, preferBackendMessage });
    throw error;
  }

  // Parse successful response as JSON
  if (res.status === 204) return null;
  const contentLength = res.headers.get('content-length');
  if (contentLength === '0') return null;
  const rawBody = await res.text();
  if (!rawBody) return null;
  let parsed;
  try {
    parsed = JSON.parse(rawBody);
  } catch (_) {
    return rawBody;
  }

  // KEEP EVERY SUCCESSFUL READ. Offline writing is useless without offline
  // reading: no register without the class list, no ID card without the pupil.
  // Caching here means a page becomes usable offline simply by having been
  // opened once online, with no per-page work.
  //
  // Deliberately not awaited — the user is waiting on this response, and a slow
  // disk must not slow the app down.
  if (String(options.method || 'GET').toUpperCase() === 'GET' && offlineAllowed(path, 'GET')) {
    putRead(url, options.headers || {}, parsed).catch(() => {});
  }

  return parsed;
}

/**
 * The queue kind for a path. Only used for the label in the pending list, so a
 * rough match is enough — but "3 marks waiting to send" reads far better than
 * "3 changes waiting to send".
 */
function offlineKindFor(path, WRITE_KINDS) {
  const p = String(path || '');
  if (/\/marks?\b/.test(p)) return WRITE_KINDS.MARKS;
  if (/\/admission/.test(p)) return WRITE_KINDS.ADMISSION;
  return WRITE_KINDS.CHANGE;
}

/** The request body as a plain object the queue can store and re-serialise. */
function parseBodyForQueue(body) {
  if (body == null) return null;
  if (typeof body === 'string') {
    try { return JSON.parse(body); } catch (_) { return body; }
  }
  return body;
}

/**
 * Whether a body survives being written to disk and sent again later.
 *
 * FormData does not: it may wrap a file handle, and JSON.stringify turns it
 * into `{}`. Queuing one would send an empty body on reconnect.
 */
function isQueueableBody(body) {
  if (body == null) return true;
  if (typeof FormData !== 'undefined' && body instanceof FormData) return false;
  if (typeof Blob !== 'undefined' && body instanceof Blob) return false;
  if (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer) return false;
  return true;
}